import React, { useEffect, useRef, useState } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { fmtCHF, fmtKM } from "../lib/constants";
import { Flame, TrendingUp, ChevronLeft, ChevronRight, ArrowRight, Eye, MessageSquare } from "lucide-react";
import { DealerBadge } from "./DealerBadge";

const FALLBACK = "https://images.unsplash.com/photo-1761229170508-f4791c297af8?w=1000&q=70&auto=format&fit=crop";

const COPY = {
  de: {
    eyebrow: "Trending Now",
    title: "Die beliebtesten Fahrzeuge dieser Woche",
    subtitle: "Live-Ranking auf Basis echter Aufrufe und Anfragen — nichts gestelltes.",
    viewAll: "Alle Inserate ansehen",
    badge: "Trending",
    today: "heute",
    week: "diese Woche",
  },
  fr: {
    eyebrow: "Tendances",
    title: "Les véhicules les plus populaires cette semaine",
    subtitle: "Classement en direct basé sur les vues et demandes réelles.",
    viewAll: "Voir toutes les annonces",
    badge: "Tendance",
    today: "aujourd'hui",
    week: "cette semaine",
  },
  it: {
    eyebrow: "Trend",
    title: "I veicoli più popolari di questa settimana",
    subtitle: "Classifica live basata su visualizzazioni e richieste reali.",
    viewAll: "Vedi tutti gli annunci",
    badge: "Trend",
    today: "oggi",
    week: "questa settimana",
  },
  en: {
    eyebrow: "Trending Now",
    title: "The hottest cars this week",
    subtitle: "Live ranking from real views and inquiries — no fake hype.",
    viewAll: "Browse all listings",
    badge: "Trending",
    today: "today",
    week: "this week",
  },
};

/** Render a small 3-bar heat indicator based on heat_score quantiles. */
function HeatLevel({ score, maxScore }) {
  const ratio = maxScore > 0 ? Math.min(1, score / maxScore) : 0;
  const level = ratio >= 0.66 ? 3 : ratio >= 0.33 ? 2 : ratio > 0 ? 1 : 0;
  if (level === 0) return null;
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`Heat level ${level}/3`}>
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={`block h-3 w-1 rounded-sm ${
            i <= level ? (level === 3 ? "bg-red-500" : level === 2 ? "bg-amber-400" : "bg-emerald-400") : "bg-zinc-800"
          }`}
        />
      ))}
    </span>
  );
}

function TrendingCard({ car, copy, maxScore, rank }) {
  const img = car.images?.[0] ? api.fileUrl(car.images[0]) : FALLBACK;
  const isHot = (car.heat_today_inquiries || 0) >= 1;
  const heatScore = car.heat_score || 0;

  return (
    <Link
      to={`/listings/${car.id}`}
      data-testid={`trending-card-${car.id}`}
      className="group relative flex w-[280px] flex-shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 transition hover:border-red-500/50 sm:w-[320px]"
    >
      {/* Rank ribbon */}
      <div className="absolute left-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-black/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-md ring-1 ring-white/10">
        <span className="tabular-nums text-zinc-400">#{rank}</span>
      </div>

      {/* Trending badge (top-right) */}
      <div
        className={`absolute right-3 top-3 z-20 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 backdrop-blur-md ${
          isHot
            ? "bg-red-600/90 text-white ring-red-400/30 shadow-lg shadow-red-900/40"
            : "bg-zinc-900/80 text-amber-300 ring-amber-500/30"
        }`}
        data-testid={`trending-badge-${car.id}`}
        data-hot={isHot ? "true" : "false"}
      >
        <Flame className={`h-3 w-3 ${isHot ? "animate-pulse" : ""}`} />
        {copy.badge}
      </div>

      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-zinc-900">
        <img
          src={img}
          alt={car.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          loading="lazy"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        {/* Bottom-overlay: title + price */}
        <div className="absolute inset-x-0 bottom-0 p-3.5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-red-300">{car.brand}</p>
          <h3 className="mt-0.5 line-clamp-1 font-display text-base font-bold text-white sm:text-lg">{car.title}</h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 p-3.5">
        <div className="flex items-baseline justify-between">
          <p className="font-display text-xl font-bold text-red-500" data-testid={`trending-price-${car.id}`}>
            {fmtCHF(car.price_chf)}
          </p>
          <HeatLevel score={heatScore} maxScore={maxScore} />
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
          {car.year && <span>{car.year}</span>}
          {car.mileage_km != null && <span>· {fmtKM(car.mileage_km)} km</span>}
          {car.location && <span>· {car.location}</span>}
        </div>

        {/* Phase 65 — verified dealer badge on trending cards */}
        {car.seller_dealer?.verified && (
          <DealerBadge dealer={car.seller_dealer} />
        )}

        {/* Activity row */}
        <div className="mt-auto flex items-center gap-3 border-t border-zinc-900 pt-3 text-[11px] text-zinc-400">
          {(car.heat_today_views || 0) > 0 && (
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3 w-3" /> {car.heat_today_views} {copy.today}
            </span>
          )}
          {(car.heat_today_inquiries || 0) > 0 && (
            <span className="inline-flex items-center gap-1 text-red-300">
              <MessageSquare className="h-3 w-3" /> {car.heat_today_inquiries} {copy.today}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function TrendingNow({ locale = "de", limit = 12 }) {
  const [items, setItems] = useState(null); // null = loading
  const scrollerRef = useRef(null);
  const copy = COPY[locale] || COPY.de;

  useEffect(() => {
    let alive = true;
    api.trendingListings({ limit })
      .then((res) => alive && setItems(Array.isArray(res?.items) ? res.items : []))
      .catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [limit]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.min(el.clientWidth * 0.85, 600);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // CLS-safe: reserve fixed height for the rail while loading
  if (items === null) {
    return (
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="trending-loading" aria-hidden="true">
        <div className="h-7 w-40 rounded bg-zinc-900" />
        <div className="mt-3 h-9 w-3/4 rounded bg-zinc-900" />
        <div className="mt-8 flex gap-4 overflow-hidden">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-[360px] w-[280px] flex-shrink-0 rounded-2xl bg-zinc-950 sm:w-[320px]" />
          ))}
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return null; // hide rail entirely on empty (rare, only fresh installs)
  }

  const maxScore = Math.max(...items.map((c) => c.heat_score || 0), 1);

  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20 lg:px-8" data-testid="trending-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-red-500">
            <Flame className="h-3.5 w-3.5 animate-pulse" /> {copy.eyebrow}
          </p>
          <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {copy.title}
          </h2>
          <p className="mt-2 max-w-xl text-sm text-zinc-400">{copy.subtitle}</p>
        </div>

        {/* Desktop controls */}
        <div className="hidden items-center gap-2 sm:flex">
          <button
            onClick={() => scrollBy(-1)}
            className="rounded-full border border-zinc-800 bg-zinc-950 p-2 text-zinc-400 transition hover:border-red-500/50 hover:text-white"
            aria-label="Scroll left"
            data-testid="trending-scroll-left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => scrollBy(1)}
            className="rounded-full border border-zinc-800 bg-zinc-950 p-2 text-zinc-400 transition hover:border-red-500/50 hover:text-white"
            aria-label="Scroll right"
            data-testid="trending-scroll-right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <Link
            to="/listings"
            className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-950 px-4 py-2 text-xs font-semibold text-zinc-300 transition hover:border-red-500/40 hover:text-white"
            data-testid="trending-view-all"
          >
            {copy.viewAll} <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative mt-8">
        {/* Edge fades on desktop */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-12 bg-gradient-to-r from-[var(--ch-bg-0)] to-transparent sm:block" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-12 bg-gradient-to-l from-[var(--ch-bg-0)] to-transparent sm:block" />

        <div
          ref={scrollerRef}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-3 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
          data-testid="trending-scroller"
        >
          {items.map((car, idx) => (
            <TrendingCard
              key={car.id}
              car={car}
              copy={copy}
              maxScore={maxScore}
              rank={idx + 1}
            />
          ))}
        </div>
      </div>

      {/* Mobile view-all */}
      <div className="mt-4 text-center sm:hidden">
        <Link
          to="/listings"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-400 hover:text-red-300"
          data-testid="trending-view-all-mobile"
        >
          {copy.viewAll} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Glow effect */}
      <TrendingGlow />
    </section>
  );
}

function TrendingGlow() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -z-10 hidden h-64 w-64 rounded-full bg-red-600/10 blur-3xl sm:block"
      style={{ left: "5%", top: "20%" }}
    />
  );
}
